import { useEffect } from "react";
import styled from "styled-components";
import { Box, Flex } from "rebass/styled-components";
import { FaSteam } from "react-icons/fa";
import { useHistory } from "react-router-dom";

import { analytics } from "MVVM/Analytics";
import { Button } from "MVVM/Components/Button";
import { usePortal } from "MVVM/Hooks/usePortal";
import { URLS } from "MVVM/Utilities/Maps";

const PROMO = { type: "modal", promotionName: "navSteambot" };

/**
 * Capture's destination is /steam-bot, which is a PrivateRoute: linking there
 * signed out means a login wall on the first click into the nav. This sells the
 * bot and hands the visitor the Steam signup instead, which is also the fastest
 * route to being eligible for it.
 *
 * A hook rather than a component because usePortal already owns the open state,
 * and both the bar trigger and the panel CTA open the same one.
 */
export const useSteambotSignupModal = () => {
  const { portalOpen, setPortalOpen, Portal } = usePortal(<SignupBody />);

  return {
    open: () => {
      analytics.track("Promotion - Button Click", PROMO);
      setPortalOpen(true);
    },
    portalOpen,
    Portal,
  };
};

const SignupBody = () => {
  const history = useHistory();

  useEffect(() => analytics.track("Promotion - Impression", PROMO), []);

  return (
    <ModalBody>
      <SteamMark>
        <FaSteam size={28} />
      </SteamMark>
      <ModalTitle>Add the Allstar Steam Bot</ModalTitle>
      <Muted>
        Add our bot on Steam and your Premier, Competitive and Wingman matches
        are picked up the moment they end, with Autocapture clips delivered
        straight to your Steam chat.
      </Muted>
      <Flex flexDirection="column" style={{ gap: 10 }}>
        <Button
          onClick={() =>
            history.push(
              `${URLS.AUTH_ROUTES.STEAM}?flow=signup&next=${encodeURIComponent(
                URLS.STEAM_BOT,
              )}`,
            )
          }
        >
          Sign up with Steam
        </Button>
        <TextButton
          onClick={() => history.push(`${URLS.LOGIN}?next=${URLS.STEAM_BOT}`)}
        >
          Already have an account? Log in
        </TextButton>
      </Flex>
    </ModalBody>
  );
};

const ModalBody = styled(Flex)`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.ice};
  flex-direction: column;
  gap: 16px;
  max-width: 440px;
  padding: 28px;
  width: 90vw;
`;

const SteamMark = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.envy};
`;

const ModalTitle = styled(Box)`
  font-family: "Barlow", sans-serif;
  font-size: 20px;
  font-weight: 700;
`;

const Muted = styled(Box)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  line-height: 1.5;
`;

const TextButton = styled(Box)`
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-size: 13px;
  text-align: center;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;
